import { EmployerSignupStatus, GetEmployerDto } from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { StrictExtract } from '@zorro/types';
import { $enum } from 'ts-enum-util';

export type HandleStepSubmit = () => Promise<
  { navigate: false } | { navigate: true; employerId: string }
>;

export enum AddEmployerStepType {
  COMPANY_DETAILS = 'COMPANY_DETAILS',
  CONTACTS = 'CONTACTS',
  COMPANY_SETUP = 'COMPANY_SETUP',
  ZORRO_SETUP = 'ZORRO_SETUP',
  PLAN_YEAR_SETUP = 'PLAN_YEAR_SETUP',
  REVIEW = 'REVIEW',
  FINALIZE = 'FINALIZE',
}

type AddEmployerRoutes = StrictExtract<
  typeof APP_ROUTES,
  | 'DON_ADD_EMPLOYER_COMPANY_DETAILS'
  | 'OPS_ADD_EMPLOYER_COMPANY_DETAILS'
  | 'DON_ADD_EMPLOYER_CONTACTS'
  | 'OPS_ADD_EMPLOYER_CONTACTS'
  | 'DON_ADD_EMPLOYER_COMPANY_SETUP'
  | 'OPS_ADD_EMPLOYER_COMPANY_SETUP'
  | 'OPS_ADD_EMPLOYER_ZORRO_SETUP'
  | 'OPS_ADD_EMPLOYER_PLAN_YEAR_SETUP'
  | 'DON_ADD_EMPLOYER_REVIEW'
  | 'OPS_ADD_EMPLOYER_REVIEW'
  | 'OPS_ADD_EMPLOYER_FINALIZE'
>;

export const getAddEmployerStepConfig = (
  step: AddEmployerStepType,
  isDon: boolean = false,
  hasNextPaymentsStep: boolean = false
) => {
  return $enum.mapValue(step).with<{
    nextRoute?: AddEmployerRoutes;
    route: AddEmployerRoutes;
    shouldShowMissingDetails: boolean;
    stepIndex: number;
    label: string;
  }>({
    [AddEmployerStepType.COMPANY_DETAILS]: {
      route: isDon
        ? 'DON_ADD_EMPLOYER_COMPANY_DETAILS'
        : 'OPS_ADD_EMPLOYER_COMPANY_DETAILS',
      nextRoute: isDon
        ? 'DON_ADD_EMPLOYER_CONTACTS'
        : 'OPS_ADD_EMPLOYER_CONTACTS',
      shouldShowMissingDetails: true,
      label: 'Company details',
      stepIndex: 0,
    },
    [AddEmployerStepType.CONTACTS]: {
      route: isDon ? 'DON_ADD_EMPLOYER_CONTACTS' : 'OPS_ADD_EMPLOYER_CONTACTS',
      nextRoute: isDon
        ? 'DON_ADD_EMPLOYER_COMPANY_SETUP'
        : 'OPS_ADD_EMPLOYER_COMPANY_SETUP',
      shouldShowMissingDetails: true,
      label: 'Contacts',
      stepIndex: 1,
    },
    [AddEmployerStepType.COMPANY_SETUP]: {
      route: isDon
        ? 'DON_ADD_EMPLOYER_COMPANY_SETUP'
        : 'OPS_ADD_EMPLOYER_COMPANY_SETUP',
      nextRoute: isDon
        ? 'DON_ADD_EMPLOYER_REVIEW'
        : 'OPS_ADD_EMPLOYER_ZORRO_SETUP',
      shouldShowMissingDetails: true,
      label: 'Company setup',
      stepIndex: 2,
    },
    [AddEmployerStepType.ZORRO_SETUP]: {
      route: 'OPS_ADD_EMPLOYER_ZORRO_SETUP',
      nextRoute: 'OPS_ADD_EMPLOYER_PLAN_YEAR_SETUP',
      shouldShowMissingDetails: true,
      label: 'Zorro setup',
      stepIndex: 3,
    },
    [AddEmployerStepType.PLAN_YEAR_SETUP]: {
      route: 'OPS_ADD_EMPLOYER_PLAN_YEAR_SETUP',
      nextRoute: isDon ? 'DON_ADD_EMPLOYER_REVIEW' : 'OPS_ADD_EMPLOYER_REVIEW',
      shouldShowMissingDetails: true,
      label: 'Plan year setup',
      stepIndex: 4,
    },
    [AddEmployerStepType.REVIEW]: {
      route: isDon ? 'DON_ADD_EMPLOYER_REVIEW' : 'OPS_ADD_EMPLOYER_REVIEW',
      nextRoute:
        isDon || !hasNextPaymentsStep ? undefined : 'OPS_ADD_EMPLOYER_FINALIZE',
      shouldShowMissingDetails: false,
      label: 'Review',
      stepIndex: 5,
    },
    [AddEmployerStepType.FINALIZE]: {
      route: isDon ? 'DON_ADD_EMPLOYER_REVIEW' : 'OPS_ADD_EMPLOYER_FINALIZE',
      shouldShowMissingDetails: false,
      label: 'Finalization',
      stepIndex: 6,
    },
  });
};

export const getAddEmployerFinalizationMode = (
  employer: GetEmployerDto
): boolean => {
  return (
    employer.signupStatus === EmployerSignupStatus.NEEDS_REVIEW ||
    employer.signupStatus === EmployerSignupStatus.IN_ZORRO_SETUP
  );
};
